// Here's how your WebPageList component should be updated
import React from "react";
import { 
  Box, VStack, Flex, Text, Checkbox, Button, Badge,
  HStack, Collapse, Icon, Tooltip, Spacer
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp, FaTimes } from "react-icons/fa";

const WebPageList = ({
  filteredApiData,
  apiData,
  checkedPages,
  handleCheckboxChange,
  expandedIndex,
  toggleExpand,
  handleRemovePage,
  cardBg,
  accentColor,
  collapseBg,
  borderColor,
  websiteUrl // Make sure this prop is passed from parent
}) => {
  const isEssentialPage = (pageUrl) => {
    return (
      pageUrl === websiteUrl || 
      pageUrl.toLowerCase() === 'header' || 
      pageUrl.toLowerCase() === 'footer'
    );
  };

  // Helper to get size category
  const getPageSizeCategory = (length) => {
    if (length < 1000) return { label: "Small", color: "blue" };
    if (length <= 10000) return { label: "Medium", color: "green" };
    return { label: "Large", color: "orange" };
  };
  
  return (
    <VStack spacing={3} align="stretch">
      {filteredApiData.length === 0 ? (
        <Box p={5} textAlign="center" color="gray.500">
          No web pages found. Extract website data first or try a different search term.
        </Box>
      ) : (
        filteredApiData.map((page, index) => {
          const originalIndex = apiData.findIndex(p => p === page);
          const isChecked = checkedPages.includes(originalIndex);
          const isEssential = isEssentialPage(page.url);
          const sizeInfo = getPageSizeCategory(page.length || 0);
          
          return (
            <Box 
              key={originalIndex}
              mb={2} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={isEssential ? "orange.300" : borderColor}
              boxShadow={isEssential ? "0 0 0 1px orange" : "none"}
              bg={cardBg}
              overflow="hidden"
            >
              <Flex 
                p={3} 
                cursor="pointer"
                onClick={() => toggleExpand(originalIndex)}
                justifyContent="space-between"
                alignItems="center"
              >
                <HStack spacing={3} flex="1">
                  <Checkbox 
                    isChecked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheckboxChange(originalIndex);
                    }}
                    colorScheme="blue"
                    isDisabled={isEssential} // Disable checkbox for essential pages
                  />
                  <Box>
                    <Text fontWeight={isEssential ? "bold" : "normal"}>
                      {page.url}
                      {isEssential && (
                        <Badge ml={2} colorScheme="orange" variant="solid">
                          Essential
                        </Badge>
                      )}
                    </Text>
                    <HStack mt={1} spacing={2}>
                      <Badge colorScheme={sizeInfo.color} variant="subtle">
                        {sizeInfo.label}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {(page.length || 0).toLocaleString()} chars
                      </Text>
                    </HStack>
                  </Box>
                </HStack>
                
                <HStack>
                  {!isEssential && (
                    <Tooltip label="Remove page" placement="top">
                      <Button
                        size="sm"
                        onClick={(e) => handleRemovePage(originalIndex, e)}
                        variant="ghost"
                        colorScheme="red"
                        isRound
                      >
                        <Icon as={FaTimes} boxSize={3} />
                      </Button>
                    </Tooltip>
                  )}
                  <Icon 
                    as={expandedIndex === originalIndex ? FaChevronUp : FaChevronDown} 
                    boxSize={4}
                  />
                </HStack>
              </Flex>
              
              <Collapse in={expandedIndex === originalIndex} animateOpacity>
                <Box p={4} bg={collapseBg}>
                  <Text 
                    fontSize="sm" 
                    whiteSpace="pre-wrap" 
                    overflowWrap="anywhere" 
                    maxHeight="300px" 
                    overflowY="auto"
                  >
                    {page.contextText}
                  </Text>
                </Box>
              </Collapse>
            </Box>
          );
        })
      )}
    </VStack>
  );
};

export default WebPageList;